import { VisitorVendorState } from '../../../../api/visitor-vendor-state/visitor-vendor-state.api';

/**
 * Vendor status types based on the state analysis
 */
export type VendorStatusType =
  | 'preparation-error' // Missing required field (phone, name, etc.)
  | 'not-eligible' // Booking status not eligible for sync
  | 'drift' // Data out of sync OR registered but has errors (needs unregister)
  | 'synced' // Everything is in sync
  | 'registered' // Has snapshot, in sync
  | 'not-registered' // No snapshot (might be expected or not)
  | 'unknown'; // No sync data

export interface VendorStatusInfo {
  type: VendorStatusType;

  /** Human-readable summary of the status */
  summary: string;

  /** Specific error details (for preparation errors) */
  errorDetails?: {
    field?: 'phone' | 'name' | 'contact' | 'events' | string;
    message?: string;
  };

  /** Eligibility details (for not_eligible) */
  eligibilityDetails?: {
    reason: string;
    message?: string;
  };

  /** Whether this status represents an error state (red) */
  isError: boolean;

  /** Whether this status represents a warning state (yellow/orange) */
  isWarning: boolean;

  /** Whether the vendor should be registered based on current state */
  expectedRegistered: boolean;

  /** Whether the vendor is actually registered */
  isRegistered: boolean;
}

/**
 * Analyzes a vendor state and returns comprehensive status information.
 * This is the single source of truth for vendor status determination.
 *
 * CRITICAL LOGIC: If a booking is registered (has stored snapshot) but has ANY
 * snapshot_error (not_eligible, preparation_error), this is DRIFT requiring unregistration.
 *
 * Priority order:
 * 1. Drift - Registered but has errors (needs unregister) OR data doesn't match
 * 2. Preparation errors (not registered, missing fields)
 * 3. Not eligible (not registered, booking status issue)
 * 4. Synced/Registered
 * 5. Not registered
 * 6. Unknown
 */
export function getVendorStatus(
  vendorState: VisitorVendorState
): VendorStatusInfo {
  const isRegistered = !!vendorState.snapshot;

  // Expected registration state logic:
  // - If 'not_eligible' error → should NOT be registered (booking status says no)
  // - If 'preparation_error' → SHOULD be registered but blocked by missing data
  // - If no error → SHOULD be registered
  const expectedRegistered =
    vendorState.snapshot_error?.status !== 'not_eligible';

  const hasDataDrift = vendorState.in_sync === false;

  // Priority 1: DRIFT - If registered but has ANY error, this is drift requiring unregistration
  if (isRegistered && vendorState.snapshot_error) {
    const errorType = vendorState.snapshot_error.status;
    const errorMessage =
      errorType === 'preparation_error'
        ? `Missing ${vendorState.snapshot_error.preparation_error_details?.field || 'field'}`
        : vendorState.snapshot_error.message || 'Not eligible';

    return {
      type: 'drift',
      summary: `Registered but ${errorMessage.toLowerCase()} - needs unregister`,
      errorDetails:
        errorType === 'preparation_error'
          ? {
              field:
                vendorState.snapshot_error.preparation_error_details?.field,
              message: vendorState.snapshot_error.message || undefined
            }
          : undefined,
      eligibilityDetails:
        errorType === 'not_eligible'
          ? {
              reason: vendorState.snapshot_error.reason,
              message: vendorState.snapshot_error.message || undefined
            }
          : undefined,
      isError: false,
      isWarning: true,
      expectedRegistered: false,
      isRegistered: true
    };
  }

  // Priority 2: Data drift (registered, no errors, but data doesn't match)
  if (hasDataDrift) {
    return {
      type: 'drift',
      summary: 'Data out of sync',
      isError: false,
      isWarning: true,
      expectedRegistered,
      isRegistered
    };
  }

  // Priority 3: Preparation errors (NOT registered, missing required fields)
  if (vendorState.snapshot_error?.status === 'preparation_error') {
    const field = vendorState.snapshot_error.preparation_error_details?.field;
    return {
      type: 'preparation-error',
      summary: field
        ? `Missing ${field}`
        : vendorState.snapshot_error.message || 'Preparation error',
      errorDetails: {
        field,
        message: vendorState.snapshot_error.message || undefined
      },
      isError: true,
      isWarning: false,
      expectedRegistered: true, // SHOULD be registered but blocked by missing data
      isRegistered
    };
  }

  // Priority 4: Not eligible (NOT registered, booking status doesn't allow sync)
  if (vendorState.snapshot_error?.status === 'not_eligible') {
    return {
      type: 'not-eligible',
      summary: vendorState.snapshot_error.message || 'Not eligible for sync',
      eligibilityDetails: {
        reason: vendorState.snapshot_error.reason,
        message: vendorState.snapshot_error.message || undefined
      },
      isError: false,
      isWarning: false,
      expectedRegistered: false,
      isRegistered
    };
  }

  // Priority 5: Synced/Registered (happy path)
  if (isRegistered) {
    return {
      type: 'synced',
      summary: 'In sync',
      isError: false,
      isWarning: false,
      expectedRegistered,
      isRegistered
    };
  }

  // Priority 6: Not registered (neutral state - either expected or never synced)
  if (!isRegistered) {
    return {
      type: 'not-registered',
      summary: 'Not registered',
      isError: false,
      isWarning: false,
      expectedRegistered,
      isRegistered
    };
  }

  // Priority 7: Unknown (fallback)
  return {
    type: 'unknown',
    summary: 'Unknown status',
    isError: false,
    isWarning: false,
    expectedRegistered,
    isRegistered
  };
}

/**
 * Helper to determine if a vendor has any problems (errors or warnings)
 */
export function hasVendorProblems(vendorState: VisitorVendorState): boolean {
  const status = getVendorStatus(vendorState);
  return status.isError || status.isWarning;
}

/**
 * Helper to get a simple status category for filtering/grouping
 */
export function getVendorStatusCategory(
  vendorState: VisitorVendorState
): 'error' | 'warning' | 'success' | 'neutral' {
  const status = getVendorStatus(vendorState);

  if (status.isError) return 'error';
  if (status.isWarning) return 'warning';
  if (status.type === 'synced' || status.type === 'registered')
    return 'success';
  return 'neutral';
}
